<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <h6 class="element-header">Kundendetails</h6>

      <div class="element-box">
        <UnemployedDetails :unemployed="unemployed"/>
        <DocumentMenu :unemployed="unemployed" :documentCounts="documentCounts"/>

        <div class="row mb-2 mt-5" style="padding: 0">
          <div class="col-lg-6">
            <h6>Digitale Dokumente</h6>
          </div>
          <div class="col-lg-6 text-right">
            <button type="button" class="btn btn-primary mb-2 mr-2"
                    style="padding: 5px 7px;"
                    @click="handleModalForm">
              Neue Dokumentengruppe anlegen
            </button>
          </div>
        </div>

        <div v-if="documentGroups.length === 0" class="empty-state-box text-center p-5 mt-4 rounded border shadow-sm">
          <div class="empty-state-icon mb-3">
            <i class="fa-solid fa-folder-open text-muted"></i>
          </div>
          <h6 class="font-weight-bold text-dark mb-1">Keine Dokumentengruppen vorhanden</h6>
          <p class="text-muted small mb-0">Bitte erstellen Sie eine neue Gruppe, um Dokumente zu verwalten.</p>
        </div>

        <div v-else class="mt-4">
          <div
              v-for="group in documentGroups"
              :key="group.uuid"
              class="mb-4 p-3 border rounded bg-white shadow-sm">

            <div class="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center border-bottom pb-3 mb-3 gap-3">

              <div class="group-info-card mini w-100 w-md-auto">
                <div class="group-details">
                  <div class="info-row">
                    <span class="label">Gruppentitel:</span>
                    <span class="value font-weight-bold text-capitalize-first">{{ group.groupTitle }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Maßnahmen:</span>
                    <span class="value text-capitalize-first">{{ group.coachingDateRange }}</span>
                  </div>
                  <div class="info-row align-center mb-3 mt-md-0">
                    <span class="label">Status:</span>
                    <span class="value text-capitalize-first">{{ group.coachingStatusTranslation }}</span>
                    <span class="status-dot orange" v-if="group.coachingStatus === 'new'"></span>
                    <span class="status-dot green"  v-if="group.coachingStatus === 'active'"></span>
                    <span class="status-dot red"    v-if="group.coachingStatus === 'finished'"></span>
                  </div>
                </div>
              </div>

              <div class="d-flex flex-column flex-sm-row flex-md-row align-items-start align-items-sm-center align-items-md-center gap-2 gap-md-3">

                <button
                    v-if="group.allDocumentSigned"
                    @click="handleAssignedCoachingModalForm(group.uuid, group.coachingId)"
                    class="btn btn-link text-primary font-weight-bold p-0 ml-1 mt-2 text-decoration-none small d-flex align-items-center"
                    style="font-size: 14px;">
                  <i class="fa fa-tasks mr-1"></i>
                  Zugewiesene Maßnahmen
                </button>

                <button
                    @click="handleModalEditForm(group.uuid, group.groupTitle)"
                    class="btn btn-link text-primary font-weight-bold p-0 ml-1 ml-md-2 mt-2 text-decoration-none small d-flex align-items-center"
                    style="font-size: 14px;">
                  <i class="fa fa-edit mr-1"></i>
                  Gruppentitel ändern
                </button>

                <button
                    @click="handleDeleteDocuments(group.uuid)"
                    class="btn btn-link text-danger font-weight-bold p-0 ml-1 ml-md-2 mt-2 text-decoration-none small d-flex align-items-center"
                    style="font-size: 14px;">
                  <i class="fa fa-trash mr-1"></i>
                  Löschen
                </button>

              </div>

            </div>

            <div v-for="(doc, index) in group.documents"
                 :key="doc.id"
                 class="row align-items-center py-3 py-md-2 mt-3 mt-md-4 mt-lg-0"
                 :class="{'border-bottom': index !== group.documents.length - 1}">

              <div class="col-12 col-lg-6 mb-3 mb-lg-0 text-left">
                <div class="d-inline-flex flex-wrap align-items-center">
                  <h6 class="mb-1 mb-sm-0 font-weight-bold text-capitalize text-dark mr-2">
                    {{ doc.title }}
                  </h6>
                  <span
                      v-if="!doc.isSigned"
                      class="badge badge-danger text-white font-weight-bold p-1 my-1 my-sm-0"
                      style="font-size: 10px; letter-spacing: 0.3px; white-space: nowrap;">
                      <i class="fa fa-exclamation-triangle mr-1"></i> Fehlende digitale Signatur
                </span>
                </div>
              </div>

              <div class="col-12 col-lg-6">
                <div
                    class="d-flex flex-column flex-sm-row justify-content-lg-end align-items-stretch align-items-sm-center w-100">

                  <button
                      @click="downloadFile(getPdfUrl(doc.type, group.uuid), undefined)"
                      class="btn btn-light border font-weight-bold px-3 py-2 py-sm-1 mb-2 mb-sm-0 mr-sm-2 d-flex align-items-center justify-content-center btn-sm"
                      title="Preuzmi PDF">
                    <img
                        src="../../../../../assets/img/pdf_icon_version4.png"
                        alt="PDF"
                        style="width: 16px; height: auto;"
                        class="mr-1"/>
                    <span style="font-size: 12px;">PDF</span>
                  </button>

                  <button
                      @click="handleDigitalSignatureModal(doc.id)"
                      class="btn btn-primary font-weight-bold px-3 py-2 py-sm-1 mb-2 mb-sm-0 mr-sm-2 ml-0 ml-md-2 d-flex align-items-center justify-content-center btn-sm"
                      style="font-size: 14px;">
                    <i class="fa fa-pencil-alt mr-1"></i>
                    Digitale Unterschrift
                  </button>

                  <button
                      @click="openForm(doc.id, doc.type)"
                      class="btn btn-outline-primary font-weight-bold px-3 py-2 py-sm-1 ml-0 ml-md-2 d-flex align-items-center justify-content-center btn-sm"
                      :class="[showFormButton(doc.type) ? 'd-flex' : 'invisible d-none d-sm-flex']"
                      style="font-size: 14px;">
                    <i class="fa fa-file-alt mr-1"></i>
                    Formular öffnen
                  </button>

                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <Modal
          v-model="showModalForm"
          title="Einführungsdokumente - Neue Gruppe"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          :customWidth="'900px'"
          :hide-footer="true"
          classFooter="justify-content-center">
        <Form
            :documentGroupUuid="selectedGroupUuid"
            :unemployedId="unemployed?.id"
            :selectedGroupId="selectedGroupUuid"
            :selectedGroupTitle="selectedGroupTitle"
            v-model:submitForm="submitForm"/>
      </Modal>

      <Modal
          v-model="showAssignedCoachingModal"
          title="Weisen Sie den digitalen Dokumenten Maßnahmen zu"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          :customWidth="'600px'"
          :hide-footer="true"
          classFooter="justify-content-center">
        <AssignCoachingForm
            :documentGroupUuid="selectedGroupUuid"
            :unemployedId="unemployed?.id"
            :selectedCoachingId="selectedCoachingId"
            v-model:submitForm="submitForm"/>
      </Modal>

      <Modal
          v-model="showDigitalSignatureModal"
          title="Digitale Unterschrift"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-md"
          :hide-footer="true"
          classFooter="justify-content-center">
        <DigitalSignatureForm
            :documentId="selectedDocumentId"
            v-model:submitForm="submitForm"/>
      </Modal>

      <Modal
          v-model="showSelectionInterviewModal"
          title="Auswahlgespräch"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-xl"
          :hide-footer="true"
          classFooter="justify-content-center">
        <SelectionInterviewForm
            :documentId="selectedDocumentId"
            v-model:submitForm="submitForm"/>
      </Modal>

      <Modal
          v-model="showFurtherTrainingModal"
          title="Weiterbildungsvertrag"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-xl"
          :hide-footer="true"
          classFooter="justify-content-center">
        <FurtherTrainingForm
            :documentId="selectedDocumentId"
            v-model:submitForm="submitForm"/>
      </Modal>

      <Modal
          v-model="showHealthQuestionnaireModal"
          title="Gesundheitsfragebogen"
          :hide-cancel="true"
          :button-label="$t('general.button.save')"
          sizeModal="modal-xl"
          :hide-footer="true"
          classFooter="justify-content-center">
        <HealthQuestionnaireForm
            :documentId="selectedDocumentId"
            v-model:submitForm="submitForm"/>
      </Modal>

      <ModalDelete
          v-model="showDeleteModal"
          :header="t('general.delete_item_header')"
          :onDelete="deleteDocumentGroup">
      </ModalDelete>
    </div>
  </PageTemplate>
</template>

<script setup lang="ts">
import unemployedService from '@/api/Unemployed';
import unemployedPrivateCoachingService from '@/api/Unemployed/PrivateCoaching/index';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalDelete from '@/components/modal/ModalDelete.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import Form from './Form.vue';
import UnemployedDetails from '@/views/App/Unemployed/UnemployedDetails.vue';
import {onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {downloadFile} from "@/shared/functions";
import DigitalSignatureForm from "@/views/App/Unemployed/DigitalDocuments/PrivateCoaching/DigitalSignatureForm.vue";
import DocumentMenu from "@/views/App/Unemployed/DigitalDocuments/DocumentMenu.vue";
import FurtherTrainingForm from "@/views/App/Unemployed/DigitalDocuments/PrivateCoaching/FurtherTrainingForm.vue";
import HealthQuestionnaireForm
  from "@/views/App/Unemployed/DigitalDocuments/PrivateCoaching/HealthQuestionnaireForm.vue";
import SelectionInterviewForm from "@/views/App/Unemployed/DigitalDocuments/PrivateCoaching/SelectionInterviewForm.vue";
import AssignCoachingForm from "@/views/App/Unemployed/DigitalDocuments/PrivateCoaching/AssignCoachingForm.vue";


export interface DocumentGroup {
  uuid: string;
  groupTitle: string;
  allDocumentSigned: boolean;
  coachingId: number|null;
  coachingDateRange: string;
  coachingStatusTranslation: string;
  coachingStatus: string;
  documents: DocumentItem[];
}

export interface DocumentItem {
  id: number;
  title: string;
  type: string;
  isSigned: boolean;
}

const {t} = useI18n();
const route = useRoute();
const unemployedId = +(route.params.id || 0);
const unemployed = ref<any>(null);
const documentCounts = ref<any>(null);
const documentGroups = ref<DocumentGroup[]>([]);
const isLoaded = ref(false);
const submitForm = ref(false);
const selectedGroupUuid = ref('');
const selectedGroupTitle = ref('');
const selectedDocumentId = ref();
const selectedCoachingId = ref()

const showModalForm = ref(false);
const showDeleteModal = ref(false);
const showDigitalSignatureModal = ref(false);
const showSelectionInterviewModal = ref(false);
const showFurtherTrainingModal = ref(false);
const showHealthQuestionnaireModal = ref(false);
const showAssignedCoachingModal = ref(false);

watch(submitForm, () => {
  selectedGroupUuid.value = '';
  selectedGroupTitle.value = '';
  closeAllModals()
  getDocumentGroups()
});

async function getUnemployedOverviewData() {
  try {
    const {data} = await unemployedService.getUnemployedOverview(unemployedId);
    unemployed.value = data.data.unemployed;
    documentCounts.value = data.data.documentCounts;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getDocumentGroups() {
  try {
    const {data} = await unemployedPrivateCoachingService.getDigitalDocuments(unemployedId);
    documentGroups.value = data.data;
  } catch (error) {
    await ErrorToast(error);
    documentGroups.value = [];
  }
}

function handleModalForm() {
  CloseToast();
  closeAllModals()
  showModalForm.value = true;
}

function handleAssignedCoachingModalForm(documentGroupUuid: string, coachingId: number|null) {
  CloseToast();
  closeAllModals()
  showAssignedCoachingModal.value = true;
  selectedGroupUuid.value = documentGroupUuid;
  selectedCoachingId.value = coachingId;
}

function handleModalEditForm(documentGroupId: string, documentGroupTitle: string) {
  CloseToast();
  closeAllModals()
  showModalForm.value = true;
  selectedGroupUuid.value = documentGroupId;
  selectedGroupTitle.value = documentGroupTitle;
}

function handleDeleteDocuments(documentGroupId: string) {
  CloseToast();
  closeAllModals()
  showDeleteModal.value = true;
  selectedGroupUuid.value = documentGroupId;
}

function handleDigitalSignatureModal(documentId: number) {
  CloseToast();
  closeAllModals()
  showDigitalSignatureModal.value = true
  selectedDocumentId.value = documentId;
}

function handleSelectionInterviewModal(documentId: number) {
  CloseToast();
  closeAllModals()
  showSelectionInterviewModal.value = true;
  selectedDocumentId.value = documentId;
}

function handleQuestionnaireModal(documentId: number) {
  CloseToast();
  closeAllModals()
  showHealthQuestionnaireModal.value = true;
  selectedDocumentId.value = documentId;
}

function handleTrainingContractModal(documentId: number) {
  CloseToast();
  closeAllModals()
  showFurtherTrainingModal.value = true
  selectedDocumentId.value = documentId;
}

function closeAllModals() {
  selectedGroupUuid.value = "";
  selectedGroupTitle.value = "";
  showModalForm.value = false;
  showDeleteModal.value = false;
  showDigitalSignatureModal.value = false;
  showSelectionInterviewModal.value = false;
  showFurtherTrainingModal.value = false;
  showHealthQuestionnaireModal.value = false;
  showAssignedCoachingModal.value = false;
}

async function deleteDocumentGroup() {
  try {
    await unemployedPrivateCoachingService.deleteDigitalDocuments(selectedGroupUuid.value);
    SuccessToast(t('general.toasts.delete_success'));
    showDeleteModal.value = false
    await getDocumentGroups();
  } catch (error) {
    await ErrorToast(error);
  }
}

function showFormButton(documentType: string): boolean {
  return [
    'selection_interview',
    'further_training_contract',
    'health_questionnaire'
  ].includes(documentType);
}

const modalHandlers: Record<string, (id: number) => void> = {
  selection_interview: handleSelectionInterviewModal,
  health_questionnaire: handleQuestionnaireModal,
  further_training_contract: handleTrainingContractModal,
};

function openForm(documentId: number, documentType: string) {
  const handler = modalHandlers[documentType];

  if (!handler) {
    ErrorToast(`No modal handler for type: ${documentType}`);
    return;
  }

  handler(documentId);
}

function getPdfUrl(type: string, groupUuid: string) {
  const mappedType = type.replaceAll('_', '-');
  return `/unemployed/private-coaching/digital-documents/${mappedType}-pdf/${groupUuid}`;
}

onMounted(async () => {
  CloseToast();

  await Promise.all([
    getUnemployedOverviewData(),
    getDocumentGroups(),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>
.empty-state-box {
  background-color: #f8f9fa;
  border-color: #e0e0e0 !important;
}

.empty-state-icon i {
  font-size: 36px;
  opacity: 0.6;
}

.group-details{
  font-size: 12px;
  letter-spacing: 0.5px;
}

.group-info-card.mini {
  font-family: sans-serif;
  color: #6c757d;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  max-width: 60%;
  line-height: 1.4;
}

@media (max-width: 767.98px) {
  .value {
    min-width: 100% !important;
  }
}

.mini .info-row {
  display: flex;
  margin-bottom: 4px;
}

.mini .info-row.align-center {
  align-items: center;
}

.mini .label {
  width: 105px;
  flex-shrink: 0;
  opacity: 0.85;
}

.mini .value {
  color: #495057;
}

.mini .status-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-left: 6px;
}

.text-capitalize-first {
  text-transform: lowercase !important;
  letter-spacing: normal !important;
  font-size: 13px;
  vertical-align: bottom;
}
.text-capitalize-first::first-letter {
  text-transform: uppercase !important;
}

.mini .status-dot {
  display: block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-left: 6px;
  flex-shrink: 0;
}

.mini .status-dot.orange { background-color: #fd7e14; }
.mini .status-dot.green  { background-color: #28a745; }
.mini .status-dot.red    { background-color: #dc3545; }

</style>

